import * as R from 'ramda';

import getFilters from '../../selectors/getSelectedFilters';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import { isFailedToFetchError } from '../../utils/requestErrorHandlers';
import fetchMediaValueReportCached, {
    isMediaValueReportRequestError,
} from './utils/fetchMediaValueReportCached';
import hasFiltersChanged from './utils/hasFiltersChanged';

import fallbackLogo from '../../images/no_image.png';

const getTopPlacementTotal = store => async state => {
    const userClientId = state.user.selectedUserClientId;
    const clientId = state.user.selectedClientId;
    const filters = state.clients[userClientId].filters;
    const client = state.user.clientsById[clientId];
    const useRightsholderAttribution = getUseRightsholderAttribution(
        client,
        clientId,
    );

    // Sets Cards as loading
    const loadingState = R.pipe(
        R.assocPath(
            [
                'clients',
                userClientId,
                'home',
                'topPlacement',
                'total',
                'isLoading',
            ],
            true,
        ),
        R.assocPath(
            [
                'clients',
                userClientId,
                'home',
                'topPlacement',
                'total',
                'hasError',
            ],
            false,
        ),
    )(state);
    store.setState(loadingState);

    const changesId = filters.changesId;
    const globFilters = getFilters(filters);

    // Get list of top 10 placements with their values
    const placementsQuery = {
        ...globFilters,
        groupBy: ['rightsholder', 'rightsholderPlacement'],
        orderBy: ['sponsorMediaValue-desc'],
        displayColumns: ['sponsorMediaValue'],
        includeTextTypeExposures: true,
        limit: 10,
        ...(useRightsholderAttribution
            ? { useRightsholderAttribution: true }
            : {}),
    };

    let result;

    try {
        result = await fetchMediaValueReportCached(placementsQuery);
    } catch (error) {
        if (
            !isFailedToFetchError(error) &&
            !isMediaValueReportRequestError(error)
        ) {
            throw error;
        }

        if (hasFiltersChanged(store, userClientId, changesId)) return;

        store.setState(
            R.pipe(
                R.assocPath(
                    [
                        'clients',
                        userClientId,
                        'home',
                        'topPlacement',
                        'total',
                        'isLoading',
                    ],
                    false,
                ),
                R.assocPath(
                    [
                        'clients',
                        userClientId,
                        'home',
                        'topPlacement',
                        'total',
                        'hasError',
                    ],
                    true,
                ),
            )(store.getState()),
            true,
        );
        return;
    }

    const rightsholdersList = filters.rightsholders?.list || {};
    const values = result?.values || [];

    const list = values.reduce((acc, v) => {
        const rhId = v.rightsholderId;
        const plcId = R.path([`${rhId}`], v.rightsholderPlacementId);

        if (rhId === undefined || plcId === undefined) {
            return acc;
        }

        const id = `${rhId}-${plcId}`;
        const rh = rightsholdersList[rhId];
        acc.push({
            id,
            name: v.rightsholderPlacementName,
            value: v.sponsorMediaValue,
            logoUrl: R.path(['mainLogo', 'url'], rh) || fallbackLogo,
            tooltip: rh?.name || v.rightsholderName || '',
        });

        return acc;
    }, []);

    if (hasFiltersChanged(store, userClientId, changesId)) return;

    store.setState(
        R.assocPath(
            ['clients', userClientId, 'home', 'topPlacement', 'total'],
            {
                isLoading: false,
                hasError: false,
                list,
                changesId,
            },
            store.getState(),
        ),
        true,
    );
};

export default getTopPlacementTotal;
